import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import logger from "../../utils/logger.js";

export default function ForgotPassword() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const labels = {
    en: {
      title: "Forgot your password?",
      subtitle:
        "Enter your email address and we will send you a link to reset your password.",
      email: "Email",
      submit: "Send reset link",
      submitting: "Sending...",
      backToLogin: "Back to login",
      generalError: "Something went wrong, please try again.",
      success:
        "If this email exists, a reset link has been sent to your inbox.",
    },
    ar: {
      title: "نسيت كلمة المرور؟",
      subtitle:
        "أدخل بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.",
      email: "البريد الإلكتروني",
      submit: "إرسال رابط إعادة التعيين",
      submitting: "جارٍ الإرسال...",
      backToLogin: "العودة لتسجيل الدخول",
      generalError: "حدث خطأ ما، حاول مرة أخرى.",
      success:
        "إن كان هذا البريد مسجلاً، سيتم إرسال رابط إعادة تعيين كلمة المرور.",
    },
  }[lang];

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email.trim()) {
      setError(isAr ? "الرجاء إدخال البريد الإلكتروني." : "Please enter your email.");
      return;
    }

    setSending(true);

    try {
      const data = await api.apiPost("forgot-password", { email });

      setSuccess(data?.message || labels.success);
    } catch (err) {
      logger.error("Forgot password error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          labels.generalError
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center"
      style={{
        direction: isAr ? "rtl" : "ltr",
        textAlign: isAr ? "right" : "left",
        background:
          "linear-gradient(135deg, #f3f4ff 0%, #e9f5ff 40%, #ffffff 100%)",
      }}
    >
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div
              className="card border-0 shadow-sm"
              style={{ borderRadius: "22px", overflow: "hidden" }}
            >
              <div className="card-body p-4 p-md-5">
                <div className="mb-4 text-center">
                  <h3 className="fw-bold mb-2" style={{ fontSize: "1.8rem" }}>
                    {labels.title}
                  </h3>
                  <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                    {labels.subtitle}
                  </p>
                </div>

                {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
                {success && <div className="alert alert-success py-2 mb-3">{success}</div>}

                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-4">
                    <label className="form-label fw-semibold">{labels.email}</label>
                    <div className="input-group">
                      <span className="input-group-text">📧</span>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 rounded-pill py-2 fw-semibold"
                    disabled={sending}
                  >
                    {sending ? labels.submitting : labels.submit}
                  </button>
                </form>

                <div className="mt-3 text-center">
                  <Link to="/login" className="text-decoration-none fw-semibold">
                    ← {labels.backToLogin}
                  </Link>
                </div>
              </div>
            </div>

            <p className="text-center text-muted mt-3 mb-0">
              © {new Date().getFullYear()} Mousa Group LLC
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}